/**
 * Canonical key combo strings, the single representation used for matching,
 * storage and display: lowercase tokens joined by '+', modifiers in a fixed
 * order (mod, ctrl, meta, alt, shift) followed by exactly one key.
 *
 *   mod   -> Cmd on Mac, Ctrl elsewhere (the "primary" modifier)
 *   ctrl  -> the physical Ctrl key on Mac only
 *   meta  -> the Windows/Super key off Mac only
 *
 * Keys come from KeyboardEvent.code so combos don't change with keyboard
 * layout: 'KeyK' -> 'k', 'Digit1' -> '1', 'Slash' -> 'slash', 'F5' -> 'f5'.
 */

export const MODIFIER_ORDER = ['mod', 'ctrl', 'meta', 'alt', 'shift'] as const

type Modifier = typeof MODIFIER_ORDER[number]

const MODIFIER_CODES = new Set([
    'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight',
    'ShiftLeft', 'ShiftRight', 'MetaLeft', 'MetaRight', 'OSLeft', 'OSRight'
])

export function isMac (): boolean {
    if (typeof navigator === 'undefined') return false
    const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform ?? ''
    return /mac|iphone|ipad|ipod/i.test(platform)
}

/** KeyboardEvent.code -> key token. */
export function codeToKey (code: string): string {
    if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase()
    if (/^Digit\d$/.test(code)) return code.slice(5)
    return code.toLowerCase()
}

export function isModifierCode (code: string): boolean {
    return MODIFIER_CODES.has(code)
}

/**
 * Normalizes any user/developer written combo into canonical form.
 * Accepts aliases (cmd, command, control, option, opt) and any order.
 * Returns null when there is no key or more than one key.
 */
export function normalizeCombo (combo: string, mac: boolean = isMac()): string | null {
    const aliases: Record<string, string> = {
        cmd: mac ? 'mod' : 'meta',
        command: mac ? 'mod' : 'meta',
        control: mac ? 'ctrl' : 'mod',
        option: 'alt',
        opt: 'alt',
        win: 'meta',
        super: 'meta'
    }
    const tokens = combo.toLowerCase().split('+').map(t => t.trim()).filter(Boolean)
    const mods = new Set<Modifier>()
    let key: string | null = null

    for (const raw of tokens) {
        let t = aliases[raw] ?? raw
        // 'ctrl' off Mac is the primary modifier
        if (t === 'ctrl' && !mac) t = 'mod'
        if ((MODIFIER_ORDER as ReadonlyArray<string>).includes(t)) {
            mods.add(t as Modifier)
        }
        else {
            if (key !== null) return null
            key = t
        }
    }
    if (key === null) return null

    return [...MODIFIER_ORDER.filter(m => mods.has(m)), key].join('+')
}

/** Canonical combo for a keydown event, or null for a lone modifier press. */
export function comboFromEvent (e: KeyboardEvent, mac: boolean = isMac()): string | null {
    if (!e.code || isModifierCode(e.code)) return null
    const mods: Array<Modifier> = []
    if (mac ? e.metaKey : e.ctrlKey) mods.push('mod')
    if (mac && e.ctrlKey) mods.push('ctrl')
    if (!mac && e.metaKey) mods.push('meta')
    if (e.altKey) mods.push('alt')
    if (e.shiftKey) mods.push('shift')

    return [...mods, codeToKey(e.code)].join('+')
}

/** True when the combo holds a modifier that makes it safe to fire while typing. */
export function hasCommandModifier (combo: string): boolean {
    return combo.split('+').some(t => t === 'mod' || t === 'ctrl' || t === 'meta' || t === 'alt')
}

const KEY_LABELS: Record<string, string> = {
    slash: '/', backslash: '\\', period: '.', comma: ',', semicolon: ';', quote: '\'',
    backquote: '`', bracketleft: '[', bracketright: ']', minus: '-', equal: '=',
    space: 'Space', enter: 'Enter', escape: 'Esc', tab: 'Tab', backspace: 'Backspace', delete: 'Del',
    arrowup: '↑', arrowdown: '↓', arrowleft: '←', arrowright: '→',
    pageup: 'PgUp', pagedown: 'PgDn', home: 'Home', end: 'End'
}

/** Display tokens, e.g. ['⌘', '⇧', 'K'] on Mac or ['Ctrl', 'Shift', 'K'] elsewhere. */
export function comboParts (combo: string, mac: boolean = isMac()): Array<string> {
    const macLabels: Record<string, string> = { mod: '⌘', ctrl: '⌃', alt: '⌥', shift: '⇧' }
    const pcLabels: Record<string, string> = { mod: 'Ctrl', meta: 'Win', alt: 'Alt', shift: 'Shift' }
    const labels = mac ? macLabels : pcLabels

    return combo.split('+').map(t => {
        if (labels[t]) return labels[t]
        if (KEY_LABELS[t]) return KEY_LABELS[t]
        return t.length === 1 ? t.toUpperCase() : t.charAt(0).toUpperCase() + t.slice(1)
    })
}

export function formatCombo (combo: string, mac: boolean = isMac()): string {
    return comboParts(combo, mac).join(mac ? '' : '+')
}
